// hooks/useUsers.tsx

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import {
  createUser,
  deleteUser,
  getAllActivityLogs,
  getAllUsers,
  getUserByEmail,
  getUserById,
  registerUser,
  sendOtp,
  updateUser,
  verifyOtp,
} from '@/services/user/userService';
import { RegisterUserPayload } from '@/types/user';
import { getCurrentUser, syncUserRoleFromData, getUserRole } from '@/lib/auth';

type SendOtpVariables = { phoneNumber: string };
type VerifyOtpVariables = { phoneNumber: string; code: string };
type UpdateUserVariables = { userId: string; userData: any };

// OTP Hooks
export function useSendOtp() {
  return useMutation({
    mutationKey: ['send-otp'],
    mutationFn: async ({ phoneNumber }: SendOtpVariables) =>
      sendOtp(phoneNumber),
  });
}

export function useVerifyOtp() {
  return useMutation({
    mutationKey: ['verify-otp'],
    mutationFn: async ({ phoneNumber, code }: VerifyOtpVariables) =>
      verifyOtp(phoneNumber, code),
  });
}

export function useRegisterUser() {
  return useMutation({
    mutationKey: ['register-user'],
    mutationFn: async (userData: RegisterUserPayload) => registerUser(userData),
  });
}

// User CRUD Hooks

// Get all users (useQuery for GET operations)
export function useGetAllUsers(enabled: boolean = true) {
  return useQuery({
    queryKey: ['users', 'all'],
    queryFn: getAllUsers,
    enabled,
  });
}

// Get user by ID (useQuery for GET operations)
export function useGetUserById(userId: string, enabled: boolean = true) {
  return useQuery({
    queryKey: ['users', userId],
    queryFn: () => getUserById(userId),
    enabled: enabled && !!userId,
  });
}

// Create user (useMutation for POST operations)
export function useCreateUser() {
  return useMutation({
    mutationKey: ['create-user'],
    mutationFn: async (userData: any) => createUser(userData),
  });
}

// Update user (useMutation for PUT operations)
export function useUpdateUser() {
  return useMutation({
    mutationKey: ['update-user'],
    mutationFn: async ({ userId, userData }: UpdateUserVariables) =>
      updateUser(userId, userData),
  });
}

// Delete user (useMutation for DELETE operations)
export function useDeleteUser() {
  return useMutation({
    mutationKey: ['delete-user'],
    mutationFn: async (userId: string) => deleteUser(userId),
  });
}

// Get all activity logs (useQuery for GET operations)
export function useGetAllActivityLogs(enabled: boolean = true) {
  return useQuery({
    queryKey: ['activity-logs', 'all'],
    queryFn: getAllActivityLogs,
    enabled,
  });
}

// Sync current user role from backend with one User row (email filter), not the full user list.
export function useSyncCurrentUserRole(enabled: boolean = true) {
  const currentUser = typeof window !== 'undefined' ? getCurrentUser() : null;
  const email = currentUser?.email as string | undefined;
  const username = currentUser?.username as string | undefined;
  const syncKey = email || username || '';

  const { data: backendUser, isLoading, refetch } = useQuery({
    queryKey: ['users', 'current-role-sync', syncKey],
    queryFn: async () => {
      if (email) return getUserByEmail(email);
      if (username) return getUserById(username);
      return null;
    },
    enabled: enabled && (!!email || !!username),
    staleTime: 30_000,
    refetchOnWindowFocus: true,
    refetchInterval: 30_000,
  });

  useEffect(() => {
    if (!backendUser || isLoading) return;

    try {
      const currentRole = getUserRole();
      const backendRole = backendUser.role;

      if (backendRole && backendRole !== currentRole) {
        syncUserRoleFromData(backendUser);
      }
    } catch (error) {
      console.error('Error syncing current user role:', error);
    }
  }, [backendUser, isLoading]);

  useEffect(() => {
    const handleRoleChange = () => {
      refetch();
    };

    window.addEventListener('userRoleChanged', handleRoleChange);

    return () => {
      window.removeEventListener('userRoleChanged', handleRoleChange);
    };
  }, [refetch]);

  return { isLoading, refetch };
}
